# What is graph-driven testing?

Cairn is graph-driven testing for web apps. Your app's user behavior — login, checkout, signup, password reset — is modeled as a flow graph and committed to the repo as YAML. The graph is the spec. Tests are *derived* from it, not handwritten.

## The vocabulary

We use the model from Ammann & Offutt's *Introduction to Software Testing* — specifically the chapter on graph coverage criteria. In that vocabulary:

- A **node** is a screen (or a state of a screen — a route plus optional sub-state).
- An **edge** is a user action that moves the user from one screen to another.
- A **path** is a sequence of edges through the graph — what a test executes.
- A **test requirement** is a coverage rule: "every node visited", "every edge traversed", "every prime path covered".

You author the graph. Cairn derives tests that satisfy the coverage criteria. Drift detection answers the question: *does the live app still match the graph?*

## Why a graph, not a script

Test scripts grow without limit. A single login flow becomes "test_login_happy_path", "test_login_invalid_password", "test_login_after_password_reset" — duplicated setup, duplicated selectors, drift between copies. The graph collapses these into one structure: every variation is a path through the same nodes and edges.

When your UI changes, you patch the graph in one place. Every derived test inherits the patch.

## What makes a good Cairn graph

Three rules, applied in order:

1. **Nodes describe what the user perceives, not what the app does.** A node is the *screen the user is looking at*, not "the controller that rendered it". Implementation hidden, intent visible.
2. **Edges are user actions, not system events.** `click Sign in`, not `POST /api/auth/login`. The graph tells the story of a person using the app.
3. **Asserts capture what the user must see to believe the action worked.** A heading visible, a button enabled, a route matching. Probabilistic asserts are allowed — see the [schema reference](/docs/schema-reference).

If a node feels small enough to fit on a sticky note, you're at the right grain.

## How drift detection uses the graph

The graph is also a contract. Cairn's scanner walks your codebase + (optionally) the deployed app and answers: *for every node in the graph, does the screen still exist? For every assert, does the element still match?*

When the answer is *no*, you get a structured drift report — selector renamed, element moved, text changed — with confidence-scored auto-patch suggestions. See [drift detection workflow](/docs/drift-workflow) for the full loop.

## Further reading

- Ammann, Paul and Offutt, Jeff. *Introduction to Software Testing* (Cambridge University Press, 2nd ed.). Chapter 7 covers graph coverage criteria; the vocabulary above lives there.
- The [schema reference](/docs/schema-reference) is the precise contract for what you can write in a `.yaml` file.
